import React, { useState, useEffect, useRef } from 'react';
import { Code2, PanelLeftClose, PanelLeftOpen, Menu } from 'lucide-react';
import { CodeEditor } from './CodeEditor';
import { LivePreview } from './LivePreview';
import { FileTree } from './FileTree';
import { Selectable } from 'kysely';
import { SessionFiles } from '../helpers/schema';
import { User } from '../helpers/User';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './Tabs';
import { Button } from './Button';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from './Resizable';
import { Sheet, SheetContent, SheetTrigger } from './Sheet';
import { useIsMobile } from '../helpers/useIsMobile';
import { useVibePreview } from '../hooks/useVibePreview';
import { seedRustWasmFiles } from '../lib/vibesPreview';
import { ErrorsPanel } from './ErrorsPanel';
import { CodegenPreviewPanel } from './CodegenPreviewPanel';
import { isCodegenTemplate } from '../helpers/templateConfig';
import { XtermTerminal } from './XtermTerminal';
import styles from './SessionCenterArea.module.css';

interface SessionCenterAreaProps {
  sessionId: string;
  sessionName?: string;
  activeFile: Selectable<SessionFiles> | undefined;
  activeFileId: string | null;
  files: Selectable<SessionFiles>[];
  currentUser: User | null;
  isOwner?: boolean;
  className?: string;
  onScreenshot?: (dataUrl: string) => void;
  onAnnotate?: (dataUrl: string) => void;
  onSelectFile: (fileId: string) => void;
  onAddFile: () => void;
  onImport?: () => void;
  onTryFix?: (errorContext: string) => void;
  onOpenPackageManager?: () => void;
  onAiGenerate?: () => void;
  isInspectMode?: boolean;
  onInspect?: () => void;
  screenshotTriggerRef?: React.MutableRefObject<(() => void) | null>;
  // Mobile-specific props for external tab control
  mobileActiveTab?: 'code' | 'preview';
  onMobileTabChange?: (tab: 'code' | 'preview') => void;
}

export const SessionCenterArea: React.FC<SessionCenterAreaProps> = ({
  sessionId,
  sessionName,
  activeFile,
  activeFileId,
  files,
  currentUser,
  isOwner,
  className,
  onScreenshot,
  onAnnotate,
  onSelectFile,
  onAddFile,
  onImport,
  onTryFix,
  onOpenPackageManager,
  onAiGenerate,
  isInspectMode,
  onInspect,
  screenshotTriggerRef,
  mobileActiveTab,
  onMobileTabChange,
}) => {
  const isDev = import.meta.env.DEV;
  const [isFileTreeOpen, setIsFileTreeOpen] = useState(true);
  const [isMobileFileTreeOpen, setIsMobileFileTreeOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'code' | 'preview' | 'errors'>('code');
  const [isTerminalOpen, setIsTerminalOpen] = useState(false);

  // Auto-detect template from localStorage (set at session creation time)
  const selectedTemplate =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem(`session_template_${sessionId}`) || 'none'
      : 'none';
  // Real errors will be populated from build/preview results — no fake data
  const hardcodedErrors: { file: string; line: number; message: string }[] = [];

  // Template-based preview gating (centralized in templateConfig.ts)
  const isCodegenOnly = isCodegenTemplate(selectedTemplate);
  const isMobile = useIsMobile();
  const currentTab: 'code' | 'preview' | 'errors' =
    isMobile && mobileActiveTab ? mobileActiveTab : activeTab;
  const activeFileDirectory = activeFile?.filePath ? activeFile.filePath : '/';
  const activeFilePath = activeFile
    ? `${activeFileDirectory === '/' ? '' : activeFileDirectory.replace(/\/$/, '')}/${activeFile.fileName}`
    : '';

  // Listen for LSP go-to-definition navigation
  useEffect(() => {
    const handleNavigate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.filePath) return;
      const targetFile = files.find(
        (f) =>
          f.filePath === detail.filePath ||
          `${f.filePath || '/'}${f.fileName}`.replace(/\/+/g, '/') === detail.filePath,
      );
      if (targetFile) {
        onSelectFile(String(targetFile.id));
        setActiveTab('code');
      }
    };
    window.addEventListener('tasking.navigateToFile', handleNavigate);
    return () => window.removeEventListener('tasking.navigateToFile', handleNavigate);
  }, [files, onSelectFile]);

  // Listen for tasking.openFile event to switch to code tab
  useEffect(() => {
    const handleOpenFile = () => {
      setActiveTab('code');
      if (isMobile && onMobileTabChange) {
        onMobileTabChange('code');
      }
    };

    window.addEventListener('tasking.openFile', handleOpenFile);
    return () => {
      window.removeEventListener('tasking.openFile', handleOpenFile);
    };
  }, [isMobile, onMobileTabChange]);

  const handleTabChange = (value: string) => {
    setActiveTab(value as 'code' | 'preview' | 'errors');
    // For mobile, only handle code/preview tabs
    if (isMobile && onMobileTabChange && (value === 'code' || value === 'preview')) {
      onMobileTabChange(value as 'code' | 'preview');
    }
  };

  // Listen for console button click to toggle terminal
  useEffect(() => {
    const handleOpenTerminal = () => {
      setActiveTab('code');
      setIsTerminalOpen((prev) => !prev);
    };
    window.addEventListener('tasking.openTerminal', handleOpenTerminal);
    return () => {
      window.removeEventListener('tasking.openTerminal', handleOpenTerminal);
    };
  }, []);

  const handleOpenPreview = () => {
    setActiveTab('preview');
    if (isMobile && onMobileTabChange) {
      onMobileTabChange('preview');
    }
  };

  // Auto-select first HTML file as default page
  React.useEffect(() => {
    if (!selectedPage && files.length > 0) {
      const firstHtmlFile = files.find(
        (f) => f.fileName.endsWith('.html') || f.fileName.endsWith('.htm'),
      );
      if (firstHtmlFile) {
        const fullPath =
          firstHtmlFile.filePath === '/' || firstHtmlFile.filePath === '' || !firstHtmlFile.filePath
            ? `/${firstHtmlFile.fileName}`
            : `${firstHtmlFile.filePath.replace(/\/$/, '')}/${firstHtmlFile.fileName}`;
        setSelectedPage(fullPath);
      }
    }
  }, [files, selectedPage]);

  const handleMobileSelectFile = (fileId: string) => {
    onSelectFile(fileId);
    setIsMobileFileTreeOpen(false);
  };

  const fileTreeContent = (
    <FileTree
      files={files}
      activeFileId={activeFileId}
      onSelectFile={isMobile ? handleMobileSelectFile : onSelectFile}
      onAddFile={onAddFile}
      onImport={onImport}
      sessionId={sessionId}
      isOwner={isOwner}
      onCollapse={!isMobile ? () => setIsFileTreeOpen(false) : undefined}
    />
  );

  // Log active file changes for diagnostics
  React.useEffect(() => {
    if (!isDev) return;
    try {
      console.log(
        '[SessionCenterArea] activeFileId=',
        activeFileId,
        'activeFilePath=',
        activeFile?.filePath,
        'filesCount=',
        files.length,
      );
      if (activeFile) {
        console.log('[SessionCenterArea] activeFile:', {
          id: activeFile.id,
          fileName: activeFile.fileName,
          filePath: activeFile.filePath,
          contentLength: activeFile.content?.length || 0,
          contentPreview: (activeFile.content || '').slice(0, 200),
          language: activeFile.language,
        });
      } else {
        // Lower-severity log to avoid noisy warnings in production
        console.debug('[SessionCenterArea] No active file selected, activeFileId=', activeFileId);
      }
    } catch (e) {
      console.error('[SessionCenterArea] Error logging active file:', e);
    }
  }, [
    activeFileId,
    activeFile?.id,
    activeFile?.filePath,
    activeFile?.content,
    activeFile?.fileName,
    files.length,
    isDev,
  ]);

  // Vibes Preview API — only publish action used here (preview opens in new tab via toolbar).
  // Skip fetch to avoid 404s; publish() will create preview on demand when user clicks Publish.
  const { publish: vibesPublish } = useVibePreview(sessionId, { skipFetch: true });

  // Auto-seed Rust Wasm template on mount (if template is rust-wasm-spa)
  useEffect(() => {
    if (selectedTemplate === 'rust-wasm-spa') {
      seedRustWasmFiles(sessionId)
        .then(() => {
          vibesPublish();
        })
        .catch((err) => {
          console.error('Failed to auto-seed Rust template:', err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <Tabs value={currentTab} onValueChange={handleTabChange} className={styles.tabsRoot}>
        <div className={styles.tabsHeader}>
          <div className={styles.tabsLeft}>
            {isMobile && (
              <Sheet open={isMobileFileTreeOpen} onOpenChange={setIsMobileFileTreeOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className={styles.mobileMenuBtn}
                    aria-label="Open file tree"
                  >
                    <Menu size={18} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className={styles.mobileFileTreeSheet}>
                  <div className={styles.mobileFileTreeWrapper}>{fileTreeContent}</div>
                </SheetContent>
              </Sheet>
            )}
            <TabsList className={styles.tabsList}>
              <TabsTrigger value="code" className={styles.tabsTrigger}>
                <span>Code</span>
              </TabsTrigger>
              <TabsTrigger value="preview" className={styles.tabsTrigger}>
                <span>Preview</span>
              </TabsTrigger>
              <TabsTrigger value="errors" className={styles.tabsTrigger}>
                <span>Errors</span>
              </TabsTrigger>
            </TabsList>
          </div>
          {activeFile && currentTab === 'code' && (
            <div className={styles.tabsRight}>
              <div className={styles.filePathIndicator} title={activeFilePath}>
                <span className={styles.filePathLabel}>Editing</span>
                <span className={styles.filePathValue}>{activeFilePath}</span>
              </div>
            </div>
          )}
        </div>

        <div className={styles.contentArea}>
          <TabsContent value="code" className={styles.tabContent}>
            <div className={styles.codeLayout}>
              {!isMobile ? (
                isFileTreeOpen ? (
                  <ResizablePanelGroup direction="horizontal">
                    <ResizablePanel
                      defaultSize={20}
                      minSize={15}
                      maxSize={40}
                      className={styles.fileTreePanel}
                    >
                      <div className={styles.fileTreeWrapper}>{fileTreeContent}</div>
                    </ResizablePanel>
                    <ResizableHandle />
                    <ResizablePanel defaultSize={80}>
                      {isTerminalOpen ? (
                        <ResizablePanelGroup direction="vertical">
                          <ResizablePanel defaultSize={isTerminalOpen ? 70 : 100} minSize={30}>
                            {activeFile ? (
                              currentUser && (
                                <CodeEditor
                                  key={activeFileId || 'no-file'}
                                  file={activeFile}
                                  sessionId={sessionId}
                                  currentUser={currentUser}
                                  sessionFiles={files}
                                  selectedPage={selectedPage}
                                  onPageChange={setSelectedPage}
                                />
                              )
                            ) : (
                              <div className={styles.emptyState}>
                                <div className={styles.emptyContent}>
                                  <Code2 size={48} className={styles.emptyIcon} />
                                  <h3>No file selected</h3>
                                  <p>Select a file to start editing.</p>
                                </div>
                              </div>
                            )}
                          </ResizablePanel>
                          <ResizableHandle />
                          <ResizablePanel defaultSize={30} minSize={20} maxSize={70}>
                            <XtermTerminal sessionId={sessionId} onAiGenerate={onAiGenerate} />
                          </ResizablePanel>
                        </ResizablePanelGroup>
                      ) : (
                        <>
                          {activeFile ? (
                            currentUser && (
                              <CodeEditor
                                key={activeFileId || 'no-file'}
                                file={activeFile}
                                sessionId={sessionId}
                                currentUser={currentUser}
                                sessionFiles={files}
                                selectedPage={selectedPage}
                                onPageChange={setSelectedPage}
                              />
                            )
                          ) : (
                            <div className={styles.emptyState}>
                              <div className={styles.emptyContent}>
                                <Code2 size={48} className={styles.emptyIcon} />
                                <h3>No file selected</h3>
                                <p>Select a file to start editing.</p>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </ResizablePanel>
                  </ResizablePanelGroup>
                ) : (
                  <div className={styles.fullEditorLayout}>
                    <div className={styles.collapsedSidebar}>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setIsFileTreeOpen(true)}
                        title="Open File Explorer"
                      >
                        <PanelLeftOpen size={16} />
                      </Button>
                    </div>
                    <div className={styles.editorContainer}>
                      {isTerminalOpen ? (
                        <ResizablePanelGroup direction="vertical">
                          <ResizablePanel defaultSize={isTerminalOpen ? 70 : 100} minSize={30}>
                            {activeFile ? (
                              currentUser && (
                                <CodeEditor
                                  key={activeFileId || 'no-file'}
                                  file={activeFile}
                                  sessionId={sessionId}
                                  currentUser={currentUser}
                                  sessionFiles={files}
                                  selectedPage={selectedPage}
                                  onPageChange={setSelectedPage}
                                  onAnnotate={onAnnotate}
                                />
                              )
                            ) : (
                              <div className={styles.emptyState}>
                                <div className={styles.emptyContent}>
                                  <Code2 size={48} className={styles.emptyIcon} />
                                  <h3>No file selected</h3>
                                </div>
                              </div>
                            )}
                          </ResizablePanel>
                          <ResizableHandle />
                          <ResizablePanel defaultSize={30} minSize={20} maxSize={70}>
                            <XtermTerminal sessionId={sessionId} onAiGenerate={onAiGenerate} />
                          </ResizablePanel>
                        </ResizablePanelGroup>
                      ) : (
                        <>
                          {activeFile ? (
                            currentUser && (
                              <CodeEditor
                                key={activeFileId || 'no-file'}
                                file={activeFile}
                                sessionId={sessionId}
                                currentUser={currentUser}
                                sessionFiles={files}
                                selectedPage={selectedPage}
                                onPageChange={setSelectedPage}
                                onAnnotate={onAnnotate}
                              />
                            )
                          ) : (
                            <div className={styles.emptyState}>
                              <div className={styles.emptyContent}>
                                <Code2 size={48} className={styles.emptyIcon} />
                                <h3>No file selected</h3>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )
              ) : (
                // Mobile layout: No split pane, just editor. File tree is in sheet.
                <div className={styles.mobileEditorLayout}>
                  {activeFile ? (
                    currentUser && (
                      <CodeEditor
                        file={activeFile}
                        sessionId={sessionId}
                        currentUser={currentUser}
                        sessionFiles={files}
                        selectedPage={selectedPage}
                        onPageChange={setSelectedPage}
                        onAnnotate={onAnnotate}
                      />
                    )
                  ) : (
                    <div className={styles.emptyState}>
                      <div className={styles.emptyContent}>
                        <Code2 size={48} className={styles.emptyIcon} />
                        <h3>No file selected</h3>
                        <p>Open menu to select a file.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="preview" className={styles.tabContent} forceMount>
            {isCodegenOnly ? (
              <CodegenPreviewPanel
                templateId={selectedTemplate}
                sessionId={sessionId}
                files={files}
              />
            ) : (
              <LivePreview
                code={activeFile?.content || ''}
                language={activeFile?.language || 'html'}
                sessionId={sessionId}
                sessionName={sessionName}
                sessionFiles={files}
                onScreenshot={onScreenshot}
                onAnnotate={onAnnotate}
                onTryFix={onTryFix}
                onOpenPackageManager={onOpenPackageManager}
                isInspectMode={isInspectMode}
                onInspect={onInspect}
                screenshotTriggerRef={screenshotTriggerRef}
                className={styles.livePreview}
                selectedPage={selectedPage}
                onPageChange={setSelectedPage}
              />
            )}
          </TabsContent>
          <TabsContent value="errors" className={styles.tabContent} forceMount>
            <ErrorsPanel
              sessionId={sessionId}
              files={files}
              onSelectFile={onSelectFile}
              errors={hardcodedErrors}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};
